import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPaw,
    faCalendarAlt,
    faClock,
    faDownload,
    faRefresh,
    faFilter,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import api from '../../api/api';
import { CURRENCY_CODE, DELIVERY_STATUS } from '../../config/constants';


import { StatsCard } from '../../components/StatsCard';
import { Menu } from '@headlessui/react';
import DriverAssign from './partials/DriverAssgin';
import LoadingLogo from '../../components/Loading';
import DeliveryDetailModal from './partials/DeliveryDetails';
import Pagination from '../../components/Pagination';
import { capitalizeWords } from '../../config/utility';
import AdminCancelDelivery from './partials/AdminCancelDelivery'
import _ from 'lodash';

const AdminDelivery = () => {
    const [deliveries, setDeliveries] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [users, setUsers] = useState([]);
    const [statsData, setStatsData] = useState({
        active_drivers: 0,
    });
    const [loading, setLoading] = useState(true);
    const [isShowModalOpen, setIsShowModalOpen] = useState(false);
    const [isDriverAssginModalOpen, setIsDriverAssginModalOpen] = useState(false);
    const [isCancelDeliveryModalOpen, setIsCancelDeliveryModalOpen] = useState(false);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [assignedFilter, setAssignedFilter] = useState('all');
    const [paymentStatus, setPaymentStatus] = useState('all');
    const [paymentMethod, setPaymentMethod] = useState('all');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    // Delivery-specific filters
    const [twoWayFilter, setTwoWayFilter] = useState('all');
    const [wantCageFilter, setWantCageFilter] = useState('all');
    const [isFreeFilter, setIsFreeFilter] = useState('all');
    const [isSlotFilter, setIsSlotFilter] = useState('all');
    const [minCharge, setMinCharge] = useState('');
    const [maxCharge, setMaxCharge] = useState('');
    const [minDistance, setMinDistance] = useState('');
    const [maxDistance, setMaxDistance] = useState('');
    const [minDuration, setMinDuration] = useState('');
    const [maxDuration, setMaxDuration] = useState('');

    const [meta, setMeta] = useState({
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1,
        from: 1,
        to: 1,
    });
    const [editDelivery, setEditDelivery] = useState(null);

    const statusColorMap = {
        requested: "bg-purple-200 text-purple-800",
        finding_driver: "bg-purple-200 text-purple-800",
        driver_assigned: "bg-purple-800 text-purple-200",
        heading_to_pickup: "bg-purple-800 text-purple-200",
        arrived_at_pickup: "bg-purple-800 text-purple-200",
        en_route_to_dropoff: "bg-purple-800 text-purple-200",
        arrived_at_dropoff: "bg-purple-800 text-purple-200",
        en_route_to_return: "bg-purple-800 text-purple-200",
        arrived_at_return: "bg-purple-800 text-purple-200",
        completed: "bg-green-200 text-green-800",
        cancelled: "bg-red-200 text-red-800",
    };

    const fetchDeliveries = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/deliveries', {
                params: {
                    search: searchTerm,
                    status: statusFilter !== 'all' ? statusFilter : null,
                    driver_assigned: assignedFilter !== 'all' ? assignedFilter : null,
                    payment_status: paymentStatus !== 'all' ? paymentStatus : null,
                    payment_method: paymentMethod !== 'all' ? paymentMethod : null,
                    from_date: fromDate || null,
                    to_date: toDate || null,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    page: currentPage,
                    two_way: twoWayFilter !== 'all' ? twoWayFilter : null,
                    want_cage: wantCageFilter !== 'all' ? wantCageFilter : null,
                    is_free: isFreeFilter !== 'all' ? isFreeFilter : null,
                    is_slot: isSlotFilter !== 'all' ? isSlotFilter : null,
                    min_charge: minCharge || null,
                    max_charge: maxCharge || null,
                    min_distance: minDistance || null,
                    max_distance: maxDistance || null,
                    min_duration: minDuration || null,
                    max_duration: maxDuration || null,
                },
            });
            setDeliveries(response.data);
            setMeta(response.meta);
            setDrivers(response.drivers);
            setUsers(response.users);
            setStatsData((prev) => ({
                ...prev,
                active_drivers: response.active_drivers,
                total_delivery: response.meta.total,
            }));
            setLoading(false);
        } catch (error) {
            // Failed to fetch deliveries
            setLoading(false);
        }
    }, [searchTerm, statusFilter, assignedFilter, paymentStatus, paymentMethod, fromDate, toDate, sortBy, sortOrder, currentPage, twoWayFilter, wantCageFilter, isFreeFilter, isSlotFilter, minCharge, maxCharge, minDistance, maxDistance, minDuration, maxDuration]);

    // Debounce after wrapping fetchDeliveries in useCallback
    const debouncedFetchDeliveries = useCallback(_.debounce(fetchDeliveries, 500), [fetchDeliveries]);

    useEffect(() => {
        debouncedFetchDeliveries();
        return () => debouncedFetchDeliveries.cancel();
    }, [debouncedFetchDeliveries]);

    const handleDownloadCSV = async () => {
        try {
            setLoading(true);

            const response = await api.get('/deliveries', {
                params: {
                    search: searchTerm,
                    status: statusFilter !== 'all' ? statusFilter : null,
                    driver_assigned: assignedFilter !== 'all' ? assignedFilter : null,
                    payment_status: paymentStatus !== 'all' ? paymentStatus : null,
                    payment_method: paymentMethod !== 'all' ? paymentMethod : null,
                    from_date: fromDate || null,
                    to_date: toDate || null,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    page: currentPage,
                    two_way: twoWayFilter !== 'all' ? twoWayFilter : null,
                    want_cage: wantCageFilter !== 'all' ? wantCageFilter : null,
                    is_free: isFreeFilter !== 'all' ? isFreeFilter : null,
                    is_slot: isSlotFilter !== 'all' ? isSlotFilter : null,
                    min_charge: minCharge || null,
                    max_charge: maxCharge || null,
                    min_distance: minDistance || null,
                    max_distance: maxDistance || null,
                    min_duration: minDuration || null,
                    max_duration: maxDuration || null,
                    want_csv: 1,
                },
                responseType: 'blob',
            });

            // Create a download link for the CSV
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'deliveries_export.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setLoading(false);

        } catch (error) {
            // Failed to download CSV
        }
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setAssignedFilter('all');
        setPaymentStatus('all');
        setPaymentMethod('all');
        setSortBy('created_at');
        setSortOrder('desc');
        setCurrentPage(1);
        setFromDate('');
        setToDate('');
        setTwoWayFilter('all');
        setWantCageFilter('all');
        setIsFreeFilter('all');
        setIsSlotFilter('all');
        setMinCharge('');
        setMaxCharge('');
        setMinDistance('');
        setMaxDistance('');
        setMinDuration('');
        setMaxDuration('');
    };

    const handleShow = (delivery) => {
        setEditDelivery(delivery);
        setIsShowModalOpen(true);
    };

    const handleCancelDelivery = (delivery) => {
        setEditDelivery(delivery);
        setIsCancelDeliveryModalOpen(true);
    };

    const handleCancel = () => {
        setIsCancelDeliveryModalOpen(false);
        fetchDeliveries();
    };

    const handleNextPage = () => {
        if (meta.current_page < meta.last_page) {
            setCurrentPage(meta.current_page + 1);
        }
    };

    const handlePrevPage = () => {
        if (meta.current_page > 1) {
            setCurrentPage(meta.current_page - 1);
        }
    };

    const renderPageNumbers = () => {
        const pages = [];
        for (let i = 1; i <= meta.last_page; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    className={`px-3 py-1 rounded-full ${i === meta.current_page ? 'bg-primary text-white' : 'bg-white text-gray-700'
                        } hover:bg-primary hover:text-white mx-1`}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    if (loading) {
        return <LoadingLogo />;
    }

    return (
        <div className="mx-auto px-4 max-w-full overflow-x-hidden">
            {/* Advanced Filters Section */}
            <div className={`transition-all duration-300 ${showAdvancedFilters ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                }`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-4">

                    {/* Delivery Status */}
                    <div className="flex flex-col">
                        <label htmlFor="status-filter" className="text-tblue font-medium mb-1">
                            Status
                        </label>
                        <select className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
                            <option value="all">All Deliveries</option>
                            <option value="ongoing">Ongoing Deliveries</option>
                            <option value="scheduled">Scheduled Deliveries</option>
                            {Object.entries(DELIVERY_STATUS).map(([key, value]) => (
                                <option key={key} value={value}>{capitalizeWords(value) + ' Deliveries'}</option>
                            ))}
                        </select>
                    </div>

                    {/* Driver Assignment */}
                    <div className="flex flex-col">
                        <label htmlFor="driver-filter" className="text-tblue font-medium mb-1">
                            Driver Status
                        </label>
                        <select className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={assignedFilter} onChange={(e) => { setAssignedFilter(e.target.value); setCurrentPage(1); }}>
                            <option value="all">All</option>
                            <option value="assigned">Assigned</option>
                            <option value="unassigned">Unassigned</option>
                        </select>
                    </div>

                    {/* Payment Status */}
                    <div className="flex flex-col">
                        <label htmlFor="payment-status-filter" className="text-tblue font-medium mb-1">
                            Payment Status
                        </label>
                        <select className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={paymentStatus} onChange={(e) => { setPaymentStatus(e.target.value); setCurrentPage(1); }}>
                            <option value="all">All</option>
                            <option value="paid">Paid</option>
                            <option value="unpaid">Unpaid</option>
                        </select>
                    </div>

                    {/* Payment Method */}
                    <div className="flex flex-col">
                        <label htmlFor="payment-method-filter" className="text-tblue font-medium mb-1">
                            Payment Method
                        </label>
                        <select className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={paymentMethod} onChange={(e) => { setPaymentMethod(e.target.value); setCurrentPage(1); }}>
                            <option value="all">All</option>
                            <option value="MamoPay">Online Payment</option>
                            <option value="Cash On Delivery">Cash on Delivery</option>
                        </select>
                    </div>

                    {/* Two Way Filter */}
                    <div className="flex flex-col">
                        <label htmlFor="two-way-filter" className="text-tblue font-medium mb-1">
                            Two Way
                        </label>
                        <select className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={twoWayFilter} onChange={(e) => { setTwoWayFilter(e.target.value); setCurrentPage(1); }}>
                            <option value="all">All</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </select>
                    </div>

                    {/* Want Cage Filter */}
                    <div className="flex flex-col">
                        <label htmlFor="cage-filter" className="text-tblue font-medium mb-1">
                            Want Cage
                        </label>
                        <select className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={wantCageFilter} onChange={(e) => { setWantCageFilter(e.target.value); setCurrentPage(1); }}>
                            <option value="all">All</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </select>
                    </div>

                    {/* Is Free Filter */}
                    <div className="flex flex-col">
                        <label htmlFor="free-filter" className="text-tblue font-medium mb-1">
                            Is Free
                        </label>
                        <select className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={isFreeFilter} onChange={(e) => { setIsFreeFilter(e.target.value); setCurrentPage(1); }}>
                            <option value="all">All</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </select>
                    </div>

                    {/* Is Slot Filter */}
                    <div className="flex flex-col">
                        <label htmlFor="slot-filter" className="text-tblue font-medium mb-1">
                            Is Slot
                        </label>
                        <select className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={isSlotFilter} onChange={(e) => { setIsSlotFilter(e.target.value); setCurrentPage(1); }}>
                            <option value="all">All</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </select>
                    </div>

                    {/* Charge Range */}
                    <div className="flex flex-col">
                        <label htmlFor="min-charge" className="text-tblue font-medium mb-1">
                            Min Charge
                        </label>
                        <input
                            type="number"
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={minCharge}
                            onChange={(e) => { setMinCharge(e.target.value); setCurrentPage(1); }}
                            placeholder="Min amount"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="max-charge" className="text-tblue font-medium mb-1">
                            Max Charge
                        </label>
                        <input
                            type="number"
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={maxCharge}
                            onChange={(e) => { setMaxCharge(e.target.value); setCurrentPage(1); }}
                            placeholder="Max amount"
                        />
                    </div>

                    {/* Distance Range */}
                    <div className="flex flex-col">
                        <label htmlFor="min-distance" className="text-tblue font-medium mb-1">
                            Min Distance (km)
                        </label>
                        <input
                            type="number"
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={minDistance}
                            onChange={(e) => { setMinDistance(e.target.value); setCurrentPage(1); }}
                            placeholder="Min km"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="max-distance" className="text-tblue font-medium mb-1">
                            Max Distance (km)
                        </label>
                        <input
                            type="number"
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={maxDistance}
                            onChange={(e) => { setMaxDistance(e.target.value); setCurrentPage(1); }}
                            placeholder="Max km"
                        />
                    </div>

                    {/* Duration Range */}
                    <div className="flex flex-col">
                        <label htmlFor="min-duration" className="text-tblue font-medium mb-1">
                            Min Duration (min)
                        </label>
                        <input
                            type="number"
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={minDuration}
                            onChange={(e) => { setMinDuration(e.target.value); setCurrentPage(1); }}
                            placeholder="Min minutes"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="max-duration" className="text-tblue font-medium mb-1">
                            Max Duration (min)
                        </label>
                        <input
                            type="number"
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={maxDuration}
                            onChange={(e) => { setMaxDuration(e.target.value); setCurrentPage(1); }}
                            placeholder="Max minutes"
                        />
                    </div>

                    {/* Date Filters */}
                    <div className="flex flex-col">
                        <label htmlFor="from-date" className="text-tblue font-medium mb-1">
                            From Date
                        </label>
                        <input
                            type="date"
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={fromDate}
                            onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
                        />
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="to-date" className="text-tblue font-medium mb-1">
                            To Date
                        </label>
                        <input
                            type="date"
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={toDate}
                            onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
                        />
                    </div>

                    {/* Sorting */}
                    <div className="flex flex-col">
                        <label htmlFor="sort-by" className="text-tblue font-medium mb-1">
                            Sort By
                        </label>
                        <select className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={sortBy} onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}>
                            <option value="created_at">Date Created</option>
                            <option value="scheduled_at">Scheduled Date</option>
                            <option value="charge">Charge</option>
                            <option value="status">Status</option>
                            <option value="google_distance">Distance</option>
                            <option value="google_duration">Duration</option>
                        </select>
                    </div>

                    {/* Sort Order */}
                    <div className="flex flex-col">
                        <label htmlFor="sort-order" className="text-tblue font-medium mb-1">
                            Sort Order
                        </label>
                        <select className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                            value={sortOrder} onChange={(e) => { setSortOrder(e.target.value); setCurrentPage(1); }}>
                            <option value="desc">Descending</option>
                            <option value="asc">Ascending</option>
                        </select>
                    </div>

                    {/* CSV Export */}
                    <div className="flex flex-col">
                        <label htmlFor="csv-export" className="text-tblue font-medium mb-1">
                            CSV Export
                        </label>
                        <button onClick={handleDownloadCSV} className="border p-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition duration-200">
                            <FontAwesomeIcon icon={faDownload} className="mr-2" />
                            Download
                        </button>
                    </div>

                </div>
            </div>

            {/* Header Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
                <h1 className="text-[#3E358F] text-3xl font-bold">Taxi Overview</h1>

                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
                    {showAdvancedFilters && (
                        <button
                            onClick={handleResetFilters}
                            className="px-3 py-2 text-sm font-medium bg-red-500 text-white rounded-lg shadow-md hover:bg-red-600 transition-all duration-300 w-full sm:w-auto"
                        >
                            <FontAwesomeIcon icon={faRefresh} className="mr-2" />
                            Reset Filters
                        </button>
                    )}

                    <button
                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                        className="bg-tblue text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 shadow-md hover:bg-[#2c257e] transition-all duration-300 w-full sm:w-auto"
                    >
                        <FontAwesomeIcon icon={faFilter} />
                        Filters
                    </button>
                </div>
            </div>

            {/* Stats Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <StatsCard title="Total Taxi" value={statsData?.total_delivery} />
                <StatsCard title="Active Drivers" value={statsData?.active_drivers} />
            </div>

            {/* Table Section */}
            <div className="overflow-x-auto relative rounded-lg">
                <table className="min-w-full bg-white rounded-lg shadow-md">
                    <thead className="bg-purple-600 text-white">
                        <tr>
                            <th className="py-4 px-4 text-left sticky left-0 bg-purple-600 z-10 border-r border-white">ID</th>
                            <th className="py-4 px-4 text-left">Driver</th>
                            <th className="py-4 px-4 text-left">Pets</th>
                            <th className="py-4 px-4 text-left">Scheduled At</th>
                            <th className="py-4 px-4 text-left">Payment Status</th>
                            <th className="py-4 px-4 text-left">Payment Method</th>
                            <th className="py-4 px-4 text-left">Charge</th>
                            <th className="py-4 px-4 text-left">Status</th>
                            <th className="py-4 px-4 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {deliveries.length > 0 ? (
                            deliveries.map((delivery, index) => (
                                <tr key={index} className="border-b hover:bg-gray-50">
                                    <td className="py-3 px-4 font-semibold text-gray-700 whitespace-nowrap sticky left-0 bg-white z-10 border-r border-gray-200">
                                        #{delivery.id}
                                    </td>

                                    <td className="py-3 px-4">
                                        <div className="flex items-center space-x-2">
                                            <FontAwesomeIcon icon={faPaw} className="text-primary" />
                                            <span className="truncate">
                                                {delivery.driver ? delivery.driver.name : 'Unassigned'}
                                            </span>
                                        </div>
                                    </td>

                                    {/* Pet Images */}
                                    <td className="px-4 py-3 sm:px-6 sm:py-4">
                                        <div className="flex -space-x-2">
                                            {delivery.pets && delivery.pets.length > 0 ? (
                                                delivery.pets.slice(0, 3).map((pet, index) => (
                                                    <img
                                                        key={index}
                                                        src={pet.primary_image_url}
                                                        alt={pet.name}
                                                        className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover shadow-md border-2 border-white"
                                                        style={{ zIndex: delivery.pets.length - index }}
                                                    />
                                                ))
                                            ) : (
                                                <span className="text-xs sm:text-sm">No pets</span>
                                            )}
                                            {delivery.pets && delivery.pets.length > 3 && (
                                                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-bold text-white border-2 border-white shadow-md">
                                                    +{delivery.pets.length - 3}
                                                </div>
                                            )}
                                        </div>
                                    </td>

                                    {/* Scheduled At */}
                                    <td className="px-4 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                                        {delivery.scheduled_at ?
                                            <div className="flex items-center space-x-2">
                                                <div className="flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-lg border border-blue-200">
                                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-sm" />
                                                    <span className="text-sm font-medium">
                                                        {new Date(delivery.scheduled_at).toLocaleString('en-US', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: true
                                                        })}
                                                    </span>
                                                </div>
                                            </div> :
                                            <span className="px-2 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">Instant</span>
                                        }
                                    </td>

                                    <td className="px-4 py-3 sm:px-6 sm:py-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${delivery.payment_status === 1 ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                                            {delivery.payment_status === 1 ? 'Paid' : 'Not Paid'}
                                        </span>
                                    </td>

                                    <td className="px-4 py-3 sm:px-6 sm:py-4">
                                        {delivery.payment_method ? (
                                            delivery.payment_method === "MamoPay" ? (
                                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-200 text-blue-800">
                                                    MamoPay
                                                </span>
                                            ) : (
                                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-200 text-gray-800">
                                                    Cash on Delivery
                                                </span>
                                            )
                                        ) : (
                                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-200 text-yellow-800">
                                                Not Selected
                                            </span>
                                        )}
                                    </td>

                                    <td className="px-4 py-3 sm:px-6 sm:py-4">
                                        {delivery.charges && delivery.charges.total ? (
                                            <span className="text-xs sm:text-sm">{delivery.charges.total} {CURRENCY_CODE}</span>
                                        ) : (
                                            'N/A'
                                        )}
                                    </td>

                                    {/* Status */}
                                    <td className="px-4 py-3 sm:px-6 sm:py-4">
                                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-bold ${statusColorMap[delivery.status]}`}>
                                            {capitalizeWords(delivery.status)}
                                        </span>
                                    </td>

                                    {/* Actions */}
                                    <td className="px-4 py-3 sm:px-6 sm:py-4">
                                        <Menu as="div" className="relative inline-block text-left">
                                            <Menu.Button className="text-gray-500 hover:text-gray-700 cursor-pointer ">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth="2"
                                                    stroke="currentColor"
                                                    className="w-6 h-6"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M12 6.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12 18.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
                                                    />
                                                </svg>
                                            </Menu.Button>

                                            <Menu.Items className="absolute right-5 -top-10 w-40 bg-bodyMain shadow-lg rounded-lg border border-accent focus:outline-none">
                                                <div className="py-1">
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <button
                                                                className={`${active ? 'bg-purple-600 text-white' : 'text-gray-700'
                                                                    } group flex rounded-md items-center justify-center w-full px-4 py-2 text-sm`}
                                                                onClick={() => handleShow(delivery)}
                                                            >
                                                                View Details
                                                            </button>
                                                        )}
                                                    </Menu.Item>
                                                    {(delivery.status === DELIVERY_STATUS.REQUESTED || delivery.status === DELIVERY_STATUS.FINDING_DRIVER) && (
                                                        <Menu.Item>
                                                            {({ active }) => (
                                                                <button
                                                                    className={`${active ? 'bg-red-600 text-white' : 'text-red-600'} group flex rounded-md items-center justify-center w-full px-4 py-2 text-sm`}
                                                                    onClick={() => handleCancelDelivery(delivery)}
                                                                >
                                                                    Cancel Delivery
                                                                </button>
                                                            )}
                                                        </Menu.Item>
                                                    )}
                                                    {delivery.driver == null && (
                                                        <Menu.Item>
                                                            {({ active }) => (
                                                                <button
                                                                    className={`${active ? 'bg-purple-600 text-white' : 'text-gray-700'
                                                                        } group flex rounded-md items-center justify-center w-full px-4 py-2 text-sm`}
                                                                    onClick={() => {
                                                                        setEditDelivery(delivery);
                                                                        setIsDriverAssginModalOpen(true);
                                                                    }}
                                                                >
                                                                    Assign Driver
                                                                </button>
                                                            )}
                                                        </Menu.Item>
                                                    )}

                                                </div>
                                            </Menu.Items>
                                        </Menu>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="py-8 text-center text-gray-500">
                                    No deliveries found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Smart Pagination */}
            <Pagination
                meta={meta}
                onPageChange={(page) => setCurrentPage(page)}
            />

            {/* Modals */}
            {isShowModalOpen && (
                <DeliveryDetailModal
                    delivery={editDelivery}
                    isOpen={isShowModalOpen}
                    onClose={() => setIsShowModalOpen(false)}
                />
            )}

            {isDriverAssginModalOpen && (
                <DriverAssign
                    delivery={editDelivery}
                    isOpen={isDriverAssginModalOpen}
                    onClose={() => {
                        setIsDriverAssginModalOpen(false);
                        fetchDeliveries();
                    }}
                    drivers={drivers}
                />
            )}

            {isCancelDeliveryModalOpen && (
                <AdminCancelDelivery
                    onCancel={handleCancel}
                    isOpen={isCancelDeliveryModalOpen}
                    delivery={editDelivery}
                />
            )}
        </div>
    );
};

export default AdminDelivery;
